import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Setup axios to always send session token from sessionStorage as fallback
axios.interceptors.request.use(config => {
  if (config.url?.includes('/api/')) {
    config.withCredentials = true;
    const token = sessionStorage.getItem('session_token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Restore user from sessionStorage for instant render (no flash)
    try {
      const cached = sessionStorage.getItem('user_data');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(!sessionStorage.getItem('user_data'));

  const setUserAndStore = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      sessionStorage.setItem('user_data', JSON.stringify(userData));
    } else {
      sessionStorage.removeItem('user_data');
      sessionStorage.removeItem('session_token');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUserAndStore(res.data);
    } catch {
      // Only clear if no cached user — avoids flashing to login on transient errors
      if (!sessionStorage.getItem('session_token')) {
        setUserAndStore(null);
      }
    } finally {
      setLoading(false);
    }
  }, [setUserAndStore]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    setUserAndStore(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: setUserAndStore, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
